import React from 'react';
import { Badge } from '@/components/ui/badge';
import { InlineSelectCell } from './InlineSelectCell';
import { actionStatusBadgeVariant, isActionOverdue } from './actionEditModel';
import { visibleActionStatusOptions } from '../ICSConstants';
import { useHwcgFlag } from 'utils/featureFlags';
import { describeBlockers } from '../utils/actionDependencies';
import type { ActionEntity } from '../hooks/useScopedActionForm';

type StatusCellProps = {
  action: ActionEntity;
  onChange: (patch: Record<string, unknown>) => void;
  disabled?: boolean;
};

/**
 * Status, plus the two things that qualify it: whether the task is past due and
 * whether anything is in the way. Both belong next to the status because a
 * "Planned" task that is overdue and blocked is a different problem from one
 * that is merely planned.
 */
export const StatusCell: React.FC<StatusCellProps> = ({
  action,
  onChange,
  disabled = false,
}) => {
  const status = action.status ?? null;
  const statusOptions = visibleActionStatusOptions(useHwcgFlag());
  const overdue = isActionOverdue(status, action.target_date);
  // A Closed task's predecessors no longer matter, whatever state they are in.
  const blocked =
    status === 'Closed' ? null : describeBlockers(action.blocked_by);

  return (
    <div>
      <InlineSelectCell
        options={statusOptions}
        value={status}
        onChange={(next) => onChange({ status: next })}
        disabled={disabled}
        ariaLabel={`Status for ${action.name || 'this task'}`}
        placeholder="Set status"
        searchPlaceholder="Search statuses"
        field="status"
      >
        <Badge
          variant={overdue ? 'destructive' : actionStatusBadgeVariant(status)}
        >
          {status || 'No status'}
        </Badge>
      </InlineSelectCell>
      {blocked && (
        <div className="tw:mt-1 tw:ps-1">
          <Badge variant="secondary" title={blocked.title}>
            {blocked.label}
          </Badge>
        </div>
      )}
      {overdue && (
        <div className="tw:mt-1 tw:ps-1 tw:text-xs tw:font-bold tw:text-destructive">
          Overdue
        </div>
      )}
    </div>
  );
};

export default StatusCell;
